/* eslint react/jsx-props-no-spreading: 0 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
} from "@material-ui/core";
import PropTypes from "prop-types";
import React from "react";
import { useDropzone } from "react-dropzone";
import { useDispatch } from "react-redux";

import { isFileFolderName } from "../../../common/FileUtil";
import { fetchUploadFile } from "../../../store/FileStore";
import useStyles from "../useStyles";

import FileActionTable from "./FileActionTable";

// 配列にオブジェクトが含まれているかの判定（true：含まれている、false：含まれていない）
const isIncludesArray = (array, object, key) => {
  for (let index = 0; index < array.length; index++) {
    if (array[index][key] === object[key]) {
      return true;
    }
  }
  return false;
};

/**
 * アップロードダイアログコンポーネント.
 *
 * @param {boolean} isOpenDialog ダイアログの表示状態
 * @param {function} onClose ダイアログクローズ時の処理
 * @param {string} filePrefix ファイルプレフィックス
 * @return {object} コンポーネント
 */
function UploadDialog(props) {
  // 引数
  const {
    // ダイアログの表示状態（true：表示、false：非表示）
    isOpenDialog,
    // ダイアログクローズ時の処理
    onClose,
    // ファイルプレフィックス
    filePrefix,
  } = props;

  // スタイル
  const styles = useStyles();

  // アップロード対象ファイル一覧
  const [targetFiles, setTargetFiles] = React.useState([]);

  const {
    // 選択されたファイル一覧
    acceptedFiles,
    // RootProps
    getRootProps,
    // InputProps
    getInputProps,
  } = useDropzone();

  // 副作用
  React.useEffect(() => {
    if (acceptedFiles && acceptedFiles.length) {
      const newAcceptedFiles = [];
      for (let index = 0; index < acceptedFiles.length; index++) {
        const acceptedFile = acceptedFiles[index];
        // ファイル名のバリデーションチェック
        // 選択されたファイルがアップロード対象ファイル一覧に含まれているか判定
        if (
          isFileFolderName(acceptedFile.name) &&
          acceptedFile.name.length <= 50 &&
          !isIncludesArray(targetFiles, acceptedFile, "path")
        ) {
          newAcceptedFiles.push(acceptedFile);
        }
      }

      if (newAcceptedFiles.length) {
        // アップロード対象ファイル一覧を更新
        setTargetFiles([...targetFiles, ...newAcceptedFiles]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedFiles]);

  // Dispatch
  const dispatch = useDispatch();

  // ダイアログクロージ時の処理
  const handleDialogClose = () => {
    setTargetFiles([]);
    onClose();
  };

  // アップロードボタン押下時の処理
  const handleUploadButtonClick = () => {
    if (!targetFiles || !targetFiles.length) {
      return;
    }
    dispatch(fetchUploadFile(filePrefix, targetFiles));
    handleDialogClose();
  };

  // 選択済みファイルの解除ボタン押下時の処理
  const handleFileCancelButtonClick = (fileInfo) => {
    // 選択済みファイル情報一覧から削除
    const newTargetFiles = targetFiles.filter(
      (targetFile) => targetFile.path !== fileInfo.path
    );
    setTargetFiles(newTargetFiles);
  };

  return (
    <Dialog
      open={isOpenDialog}
      fullWidth
      maxWidth="sm"
      onClose={handleDialogClose}
    >
      <DialogTitle>アップロード</DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <Paper
          variant="outlined"
          square
          {...getRootProps()}
          className={styles.dragAndDropPaper}
        >
          <input {...getInputProps()} />
          <Typography variant="h6">
            ここにファイルをドラッグ＆ドロップ
          </Typography>
          <Typography variant="h6">もしくは、クリックして選択</Typography>
          <br />
          <br />
          <Typography variant="body2">
            ※ 使用可能文字で50文字以内のファイルのみ選択可能
          </Typography>
        </Paper>
      </DialogContent>
      <DialogContent className={styles.dialogContent}>
        <FileActionTable
          fileInfos={targetFiles}
          onCancel={handleFileCancelButtonClick}
        />
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <Button color="default" onClick={handleDialogClose}>
          CANCEL
        </Button>
        <Button
          color="primary"
          onClick={handleUploadButtonClick}
          disabled={!targetFiles.length}
        >
          UPLOAD
        </Button>
      </DialogActions>
    </Dialog>
  );
}

UploadDialog.propTypes = {
  isOpenDialog: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  filePrefix: PropTypes.string.isRequired,
};

export default UploadDialog;
